import { useCallback, useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import useDebounce from "@/hooks/useDebounce";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useMunicipality } from "@/context/MunicipalityContext";
import { useUser } from "@/context/UserProvider";
import {
  getFileLibraryFolders,
  getFolderDocuments,
  getExactSearch,
  searchFileLibrary,
  startExactSearch,
  type FileLibraryDocument,
  type FileLibraryFolder,
  type FileLibraryFolderHit,
  type FileLibraryGroup,
  type FileLibraryPaginationMetadata,
  type FileLibrarySearchResult,
  type FolderTarget,
} from "@/api/file_library";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  exactSearchScopeText,
  fieldsFromSearchBar,
  positiveQueryFromExactSearch,
} from "@/lib/fileCenterExactSearch";
import {
  ChevronRight,
  Folder,
  Loader2,
  PanelLeftOpen,
  Search,
  SlidersHorizontal,
  Sparkles,
  X,
} from "lucide-react";
import { FileCenterFolderGrid } from "@/components/file_viewer/FileCenterFolderGrid";
import { FileCenterAdvancedSearchModal } from "@/components/file_viewer/FileCenterAdvancedSearchModal";
import { MunicipalitySwitcher } from "@/components/launch_pad/MunicipalitySwitcher";
import {
  FILE_CENTER_SIDEBAR_SCROLL_VIEWPORT_CLASS,
  FileCenterSidebarLabel,
} from "@/components/file_viewer/fileCenterLabels";
import { FileCenterTooltipProvider } from "@/components/file_viewer/fileCenterTooltips";
import { ModuleWorkspaceHeader } from "@/components/module_summary/ModuleWorkspaceHeader";
import { moduleWorkspaceShellClass } from "@/components/module_summary/moduleWorkspaceLayout";

// Documents are loaded lazily per folder, so default to the first page size; "View N more in folder"
// jumps to the center grid for full pagination rather than infinitely growing the sidebar.
const SIDEBAR_PAGE_SIZE = 25;
const GRID_PAGE_SIZE = 50;
// Caps on search matches surfaced in-tree (server clamps to these maxima); enough to expand the
// right folders and list the top document hits.
const SEARCH_DOCUMENT_LIMIT = 100;
const SEARCH_FOLDER_LIMIT = 25;
const EXACT_SEARCH_POLL_MS = 1000;
const EXACT_SEARCH_MAX_INTERVAL_MS = 8_000;
export const EXACT_SEARCH_MAX_POLLS = 40;
export const EXACT_SEARCH_DURATION_HINT_MS = 10_000;
const INTELLIGENT_SEARCH_HOLD_MS = 1000;
const INTELLIGENT_SEARCH_GRADIENT_FROM = "#5590E8";
const INTELLIGENT_SEARCH_GRADIENT_TO = "#D97AD0";

// Natural-language search wants the user's words as typed, so we trim but never lowercase (ENG-1540).
function normalizeQuery(q: string): string {
  return q.trim();
}

function exactSearchInFlight(status: FileLibrarySearchResult["status"]): boolean {
  return status === "pending" || status === "processing";
}

export function exactSearchAttemptCount(dataUpdateCount: number, errorUpdateCount: number): number {
  return Math.max(0, dataUpdateCount - 1) + errorUpdateCount;
}

export function exactSearchProcessingAttemptCount(processingFetchCount: number): number {
  return Math.max(0, processingFetchCount - 1);
}

export function exactSearchRefetchInterval(
  status: FileLibrarySearchResult["status"] | undefined,
  dataUpdateCount: number,
  errorUpdateCount: number,
  processingAttemptCount = 0,
): number | false {
  if (!exactSearchInFlight(status)) return false;
  if (status === "processing" && processingAttemptCount >= EXACT_SEARCH_MAX_POLLS) return false;
  const attempts = exactSearchAttemptCount(dataUpdateCount, errorUpdateCount);
  const backoffStep = Math.min(attempts, 4);
  return Math.min(EXACT_SEARCH_POLL_MS * 2 ** backoffStep, EXACT_SEARCH_MAX_INTERVAL_MS);
}

function clerkFacingSearchError(error: unknown): string {
  if (error instanceof Error) {
    const text = error.message.trim();
    if (text.length > 0 && text.length <= 180 && !text.includes("<") && !text.includes("{")) {
      return text;
    }
  }
  return "Search failed. Edit your query to try again.";
}

function formatFileCenterCount(count: number): string {
  return new Intl.NumberFormat().format(count);
}

function exactMatchRangeCopy(total: number, page: number, perPage: number, pageCount: number): string {
  const start = (page - 1) * perPage + 1;
  const end = start + pageCount - 1;
  return `Showing ${formatFileCenterCount(start)}–${formatFileCenterCount(end)} of ${formatFileCenterCount(total)} matching documents.`;
}

type IntelligentSearchIndicatorProps = {
  className?: string;
};

function IntelligentSearchSparkle() {
  const gradientId = useId().replace(/:/g, "");

  return (
    <>
      <svg width="0" height="0" className="absolute" aria-hidden>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={INTELLIGENT_SEARCH_GRADIENT_FROM} />
            <stop offset="100%" stopColor={INTELLIGENT_SEARCH_GRADIENT_TO} />
          </linearGradient>
        </defs>
      </svg>
      <Sparkles
        className="h-3.5 w-3.5 shrink-0 file-center-intelligent-search-sparkle"
        style={{ stroke: `url(#${gradientId})`, fill: `url(#${gradientId})` }}
        aria-hidden
      />
    </>
  );
}

function IntelligentSearchIndicator({ className }: IntelligentSearchIndicatorProps) {
  return (
    <div className={cn("flex items-center gap-2", className)} role="status" aria-live="polite">
      <IntelligentSearchSparkle />
      <span className="file-center-intelligent-search-text">AI-assisted search...</span>
    </div>
  );
}

function ExactSearchIndicator({
  className,
  label,
}: IntelligentSearchIndicatorProps & { label: string }) {
  const [showDurationHint, setShowDurationHint] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setShowDurationHint(true), EXACT_SEARCH_DURATION_HINT_MS);
    return () => window.clearTimeout(timeoutId);
  }, []);

  return (
    <div className={cn("flex flex-col gap-1", className)} role="status" aria-live="polite">
      <div className="flex items-center gap-2">
        <SlidersHorizontal
          className="h-3.5 w-3.5 shrink-0 text-muted-foreground file-center-intelligent-search-sparkle"
          aria-hidden
        />
        <span className="file-center-intelligent-search-sparkle">{label}</span>
      </div>
      {showDurationHint ? (
        <p className="text-muted-foreground">This can take up to a minute for large libraries.</p>
      ) : null}
    </div>
  );
}

function FileCenterCount({ count }: { count: number }) {
  return <span className="font-bold tabular-nums text-foreground">{formatFileCenterCount(count)}</span>;
}

function fileCenterCountLabel(count: number, singular: string, plural: string) {
  return count === 1 ? singular : plural;
}

const FILE_CENTER_FOLDER_ROW_CLASS =
  "flex w-full min-w-0 max-w-full items-center gap-2 overflow-hidden rounded-md px-2 py-1.5 pr-9 text-left text-sm hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const FILE_CENTER_SIDEBAR_FILE_ROW_CLASS =
  "flex w-full min-w-0 max-w-full items-center overflow-hidden rounded-md px-3 py-2 text-sm text-left hover:bg-muted transition-colors";

const FILE_CENTER_FOLDER_COUNT_CLASS =
  "pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 shrink-0 tabular-nums text-sm font-medium normal-case opacity-70";

// ---- folder-tree helpers (counts only; no documents) ----

function childrenByParentId(folders: FileLibraryFolder[]): Map<number | null, FileLibraryFolder[]> {
  const map = new Map<number | null, FileLibraryFolder[]>();
  for (const folder of folders) {
    const key = folder.parent_id ?? null;
    const list = map.get(key) ?? [];
    list.push(folder);
    map.set(key, list);
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.position - b.position || a.id - b.id);
  }
  return map;
}

function foldersById(folders: FileLibraryFolder[]): Map<number, FileLibraryFolder> {
  return new Map(folders.map((f) => [f.id, f]));
}

function ancestorChain(folderId: number, byId: Map<number, FileLibraryFolder>): number[] {
  const chain: number[] = [];
  let current: number | null = folderId;
  while (current != null) {
    chain.push(current);
    current = byId.get(current)?.parent_id ?? null;
  }
  return chain;
}

// The center grid still renders from FileLibraryGroup[] (id/name/parent_id for cards + breadcrumbs).
// document_ids are populated only for the browsed folder (the page we lazily loaded); other folders
// are navigation-only, so an empty list is correct.
function foldersToGroups(
  folders: FileLibraryFolder[],
  documentIdsByFolder: Map<number, number[]>,
): FileLibraryGroup[] {
  return folders.map((f) => ({
    id: f.id,
    name: f.name,
    description: f.description,
    position: f.position,
    parent_id: f.parent_id,
    document_ids: documentIdsByFolder.get(f.id) ?? [],
  }));
}

type SidebarFileButtonProps = {
  doc: FileLibraryDocument;
  selected: boolean;
  onPick: () => void;
};

function SidebarFileButton({ doc, selected, onPick }: SidebarFileButtonProps) {
  return (
    <button
      type="button"
      onClick={onPick}
      className={cn(FILE_CENTER_SIDEBAR_FILE_ROW_CLASS, selected && "bg-muted font-medium")}
    >
      <FileCenterSidebarLabel text={doc.name} />
    </button>
  );
}

type FolderHitRowProps = {
  hit: FileLibraryFolderHit;
  onPick: () => void;
};

// A semantic folder match. Shows the folder name plus its breadcrumb path so the user can tell it
// apart from a document hit and knows where in the tree it lives. Reuses the file-row styling.
function FolderHitRow({ hit, onPick }: FolderHitRowProps) {
  return (
    <button
      type="button"
      onClick={onPick}
      className={cn(FILE_CENTER_SIDEBAR_FILE_ROW_CLASS, "flex-col items-start gap-0.5")}
    >
      <span className="flex w-full min-w-0 items-center gap-2">
        <Folder className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <FileCenterSidebarLabel text={hit.name} className="font-medium" />
      </span>
      {hit.path && (
        <span className="w-full min-w-0 truncate pl-6 text-xs text-muted-foreground" title={hit.path}>
          {hit.path}
        </span>
      )}
    </button>
  );
}

type FolderBranchProps = {
  folder: FileLibraryFolder;
  childrenByParent: Map<number | null, FileLibraryFolder[]>;
  municipalityId: number;
  open: boolean;
  folderOpen: Record<number, boolean>;
  onFolderToggle: (folderId: number, open: boolean) => void;
  selectedDoc: FileLibraryDocument | null;
  onPickDocument: (doc: FileLibraryDocument) => void;
  onViewFolder: (folderId: number) => void;
  // Search mode: documents come from the grouped search results instead of a lazy fetch.
  searchActive: boolean;
  searchDocsByFolder: Map<number, FileLibraryDocument[]>;
  visibleFolderIds: Set<number> | null;
};

function FolderBranch(props: FolderBranchProps) {
  const {
    folder,
    childrenByParent,
    municipalityId,
    open,
    folderOpen,
    onFolderToggle,
    selectedDoc,
    onPickDocument,
    onViewFolder,
    searchActive,
    searchDocsByFolder,
    visibleFolderIds,
  } = props;

  // Browse mode: each open folder lazily fetches its first page of documents. Search mode disables
  // the fetch and renders the matched documents passed down from the search results.
  const { data, isFetching } = useQuery({
    queryKey: ["fileLibraryFolderDocs", municipalityId, folder.id, 1, SIDEBAR_PAGE_SIZE],
    queryFn: () => getFolderDocuments(municipalityId, folder.id, { page: 1, perPage: SIDEBAR_PAGE_SIZE }),
    enabled: !searchActive && open,
    staleTime: 60_000,
  });

  const childFolders = (childrenByParent.get(folder.id) ?? []).filter(
    (child) => visibleFolderIds == null || visibleFolderIds.has(child.id),
  );

  const docs = searchActive ? (searchDocsByFolder.get(folder.id) ?? []) : (data?.documents ?? []);
  const loadedCount = data?.metadata.total_count ?? folder.document_count;
  const hasMore = !searchActive && docs.length < loadedCount;

  return (
    <Collapsible
      open={open}
      onOpenChange={(next) => onFolderToggle(folder.id, next)}
      className="group w-full min-w-0 max-w-full overflow-hidden"
    >
      <div className="relative w-full min-w-0 max-w-full overflow-hidden">
        <CollapsibleTrigger type="button" className={FILE_CENTER_FOLDER_ROW_CLASS}>
          <ChevronRight
            className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90"
            aria-hidden
          />
          <Folder className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <FileCenterSidebarLabel
            text={folder.name}
            className="text-sm font-semibold text-muted-foreground"
          />
        </CollapsibleTrigger>
        <span className={FILE_CENTER_FOLDER_COUNT_CLASS}>
          {searchActive ? docs.length : folder.subtree_document_count}
        </span>
      </div>
      <CollapsibleContent className="min-w-0 overflow-hidden">
        <div className="mt-0.5 w-full min-w-0 max-w-full overflow-hidden space-y-0.5 border-l border-border/70 pl-3">
          {childFolders.map((child) => (
            <FolderBranch
              key={child.id}
              {...props}
              folder={child}
              open={folderOpen[child.id] ?? false}
            />
          ))}
          {isFetching && docs.length === 0 && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              Loading…
            </div>
          )}
          {docs.length > 0 && (
            <ul className="min-w-0 max-w-full space-y-0.5 overflow-hidden">
              {docs.map((doc) => (
                <li key={doc.id} className="min-w-0 max-w-full overflow-hidden">
                  <SidebarFileButton
                    doc={doc}
                    selected={selectedDoc?.id === doc.id}
                    onPick={() => onPickDocument(doc)}
                  />
                </li>
              ))}
            </ul>
          )}
          {hasMore && (
            <button
              type="button"
              onClick={() => onViewFolder(folder.id)}
              className="px-3 py-1.5 text-xs font-medium text-primary hover:underline"
            >
              View all {loadedCount} in folder
            </button>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function FileViewerExplorer() {
  const { municipalities, municipality, isLoading: municipalitiesLoading } = useMunicipality();
  const { currentUser } = useUser();
  const queryClient = useQueryClient();
  const [selectedDoc, setSelectedDoc] = useState<FileLibraryDocument | null>(null);
  const [librarySearch, setLibrarySearch] = useState("");
  const [searchMode, setSearchMode] = useState<"intelligent" | "exact">("intelligent");
  const [exactSearchNonce, setExactSearchNonce] = useState(0);
  const [submittedExactQuery, setSubmittedExactQuery] = useState("");
  const [exactFolderIds, setExactFolderIds] = useState<number[]>([]);
  const [includeUngrouped, setIncludeUngrouped] = useState(false);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [folderOpen, setFolderOpen] = useState<Record<number, boolean>>({});
  const [ungroupedOpen, setUngroupedOpen] = useState(false);
  const [browseFolderId, setBrowseFolderId] = useState<number | null>(null);
  const [gridPage, setGridPage] = useState(1);
  const [documentPreviewSidePanelOpen, setDocumentPreviewSidePanelOpen] = useState(false);
  const [librarySidebarCollapsed, setLibrarySidebarCollapsed] = useState(false);

  const documentPreviewFocus = Boolean(selectedDoc && documentPreviewSidePanelOpen);
  const fileViewerEnabled = municipality?.file_viewer_access === true;
  const municipalityId = municipality && fileViewerEnabled ? municipality.id : null;

  const debouncedLibrarySearch = useDebounce(librarySearch, 400);
  const exactSearch = searchMode === "exact";
  const liveSearchNorm = useMemo(() => {
    // Wait until debounce catches up so we do not fire search for a stale prefix of the live bar.
    if (librarySearch !== debouncedLibrarySearch) return "";
    return normalizeQuery(debouncedLibrarySearch);
  }, [librarySearch, debouncedLibrarySearch]);
  const searchNorm = exactSearch ? submittedExactQuery : liveSearchNorm;
  const searchActive = searchNorm.length > 0;
  const [intelligentSearchHoldActive, setIntelligentSearchHoldActive] = useState(false);
  const exactFolderKey = `${includeUngrouped ? "1" : "0"}:${[...exactFolderIds].sort((a, b) => a - b).join(",")}`;
  const searchPagingKey = exactSearch
    ? `${municipalityId ?? ""}:${searchMode}:${searchNorm}:${exactFolderKey}:${exactSearchNonce}`
    : `${municipalityId ?? ""}:${searchMode}:${searchNorm}`;
  const [searchPaging, setSearchPaging] = useState({ key: searchPagingKey, page: 1 });
  const searchPage = searchPaging.key === searchPagingKey ? searchPaging.page : 1;
  const lastGoodExactMetadataRef = useRef<FileLibraryPaginationMetadata | undefined>(undefined);
  const [failedExactPageNote, setFailedExactPageNote] = useState<{
    failedPage: number;
    restoredPage: number;
  } | null>(null);
  const processingFetchCountRef = useRef(0);
  const [processingPollCount, setProcessingPollCount] = useState(0);
  const [processingPollSearchId, setProcessingPollSearchId] = useState<number | null>(null);

  useEffect(() => {
    if (searchActive) return;
    queryClient.removeQueries({ queryKey: ["fileLibraryExactSearchStart", municipalityId] });
  }, [searchActive, municipalityId, queryClient]);

  useEffect(() => {
    setSearchPaging((current) =>
      current.key === searchPagingKey ? current : { key: searchPagingKey, page: 1 },
    );
    lastGoodExactMetadataRef.current = undefined;
    setFailedExactPageNote(null);
  }, [searchPagingKey]);

  useEffect(() => {
    if (exactSearch || librarySearch.trim().length === 0) {
      setIntelligentSearchHoldActive(false);
      return;
    }

    if (librarySearch !== debouncedLibrarySearch) {
      setIntelligentSearchHoldActive(true);
      return;
    }

    setIntelligentSearchHoldActive(true);
    const timer = window.setTimeout(() => {
      setIntelligentSearchHoldActive(false);
    }, INTELLIGENT_SEARCH_HOLD_MS);

    return () => window.clearTimeout(timer);
  }, [exactSearch, librarySearch, debouncedLibrarySearch]);

  useEffect(() => {
    setLibrarySearch("");
    setSearchMode("intelligent");
    setSubmittedExactQuery("");
    setExactFolderIds([]);
    setIncludeUngrouped(false);
    setAdvancedSearchOpen(false);
    setFolderOpen({});
    setUngroupedOpen(false);
    setBrowseFolderId(null);
    setGridPage(1);
    setSelectedDoc(null);
  }, [municipality?.id]);

  // 1) Folder tree + counts, loaded once.
  const {
    data: tree,
    isLoading: treeLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["fileLibraryFolders", municipalityId],
    queryFn: () => getFileLibraryFolders(municipalityId!),
    enabled: Boolean(municipalityId),
    staleTime: 60_000,
  });

  // Memoized so the array keeps a stable identity across renders (notably while `tree` is still
  // loading): otherwise `byId`/`childrenByParent` churn every render and the open-chain effect below
  // re-fires into an infinite setFolderOpen loop.
  const folders = useMemo(() => tree?.folders ?? [], [tree]);
  const childrenByParent = useMemo(() => childrenByParentId(folders), [folders]);
  const byId = useMemo(() => foldersById(folders), [folders]);
  const rootFolders = childrenByParent.get(null) ?? [];

  // 2) Documents for the currently-browsed folder (or the ungrouped/root bucket), paginated.
  const browseTarget: FolderTarget = browseFolderId ?? "ungrouped";
  const { data: browseDocs, isFetching: browseFetching } = useQuery({
    queryKey: ["fileLibraryFolderDocs", municipalityId, browseTarget, gridPage, GRID_PAGE_SIZE],
    queryFn: () =>
      getFolderDocuments(municipalityId!, browseTarget, { page: gridPage, perPage: GRID_PAGE_SIZE }),
    enabled: Boolean(municipalityId) && !searchActive && librarySearch.trim().length === 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  // 3) Search. Intelligent ranking stays in-request. Exact match starts a job, then polls.
  const intelligentQuery = useQuery({
    queryKey: ["fileLibrarySearch", municipalityId, searchNorm, searchMode],
    queryFn: ({ signal }) =>
      searchFileLibrary(municipalityId!, {
        q: searchNorm,
        folderLimit: SEARCH_FOLDER_LIMIT,
        documentLimit: SEARCH_DOCUMENT_LIMIT,
        signal,
      }),
    enabled: Boolean(municipalityId) && searchActive && !exactSearch,
    staleTime: 30_000,
  });

  const exactStartQuery = useQuery({
    queryKey: [
      "fileLibraryExactSearchStart",
      municipalityId,
      submittedExactQuery,
      exactFolderKey,
      exactSearchNonce,
    ],
    queryFn: ({ signal }) =>
      startExactSearch(municipalityId!, {
        q: submittedExactQuery,
        folderLimit: SEARCH_FOLDER_LIMIT,
        ...(exactFolderIds.length > 0 ? { folderIds: exactFolderIds } : {}),
        ...(includeUngrouped ? { includeUngrouped: true } : {}),
        signal,
      }),
    enabled: Boolean(municipalityId) && exactSearch && submittedExactQuery.length > 0,
    staleTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const exactSearchId = exactStartQuery.data?.id ?? null;
  if (processingPollSearchId !== exactSearchId) {
    setProcessingPollSearchId(exactSearchId);
    setProcessingPollCount(0);
    processingFetchCountRef.current = 0;
  }
  const exactResultQueryKey = ["fileLibraryExactSearch", municipalityId, exactSearchId, searchPage, GRID_PAGE_SIZE];
  const exactResultQuery = useQuery({
    queryKey: exactResultQueryKey,
    queryFn: async ({ signal }) => {
      try {
        const result = await getExactSearch(municipalityId!, exactSearchId!, {
          page: searchPage,
          perPage: GRID_PAGE_SIZE,
          signal,
        });
        if (result.status === "processing") {
          processingFetchCountRef.current += 1;
          setProcessingPollCount(exactSearchProcessingAttemptCount(processingFetchCountRef.current));
        }
        return result;
      } catch (error) {
        if (processingFetchCountRef.current > 0) {
          processingFetchCountRef.current += 1;
          setProcessingPollCount(exactSearchProcessingAttemptCount(processingFetchCountRef.current));
        }
        throw error;
      }
    },
    enabled: Boolean(municipalityId) && exactSearch && exactSearchId != null,
    staleTime: 30_000,
    refetchInterval: (query) =>
      exactSearchRefetchInterval(
        query.state.data?.status,
        query.state.dataUpdateCount,
        query.state.errorUpdateCount,
        exactSearchProcessingAttemptCount(processingFetchCountRef.current),
      ),
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === municipalityId && previousQuery.queryKey[2] === exactSearchId
        ? previous
        : undefined,
  });

  const searchData = exactSearch ? (exactResultQuery.data ?? exactStartQuery.data) : intelligentQuery.data;
  const exactStatus = searchData?.status;
  const exactPollExhausted =
    exactSearch && exactStatus === "processing" && processingPollCount >= EXACT_SEARCH_MAX_POLLS;
  const searchLoading = exactSearch
    ? exactStartQuery.isLoading ||
      (exactResultQuery.isLoading && !exactResultQuery.isPlaceholderData && exactResultQuery.data == null)
    : intelligentQuery.isLoading;
  const searchIsError = exactSearch
    ? exactStartQuery.isError ||
      exactResultQuery.isError ||
      exactStartQuery.data?.status === "failed" ||
      exactResultQuery.data?.status === "failed" ||
      exactPollExhausted
    : intelligentQuery.isError;
  const searchSucceeded = exactSearch
    ? exactResultQuery.isSuccess && exactResultQuery.data?.status === "completed"
    : intelligentQuery.isSuccess;
  const searchIsPlaceholderData = exactSearch
    ? exactResultQuery.isPlaceholderData
    : intelligentQuery.isPlaceholderData;

  useEffect(() => {
    if (exactSearch && searchData?.metadata && searchData.timed_out !== true) {
      lastGoodExactMetadataRef.current = searchData.metadata;
      if (
        failedExactPageNote != null &&
        searchData.metadata.current_page !== failedExactPageNote.restoredPage
      ) {
        setFailedExactPageNote(null);
      }
    }
  }, [exactSearch, searchData, failedExactPageNote]);

  useEffect(() => {
    if (!exactSearch) return;
    if (!searchIsError && searchData?.timed_out !== true) return;
    const fallbackPage = lastGoodExactMetadataRef.current?.current_page ?? 1;
    if (searchPage === fallbackPage) return;
    const failedPage = searchPage;
    setFailedExactPageNote({ failedPage, restoredPage: fallbackPage });
    setSearchPaging({ key: searchPagingKey, page: fallbackPage });
    queryClient.removeQueries({
      queryKey: [
        "fileLibraryExactSearch",
        municipalityId,
        exactSearchId,
        failedPage,
        GRID_PAGE_SIZE,
      ],
    });
  }, [
    exactSearch,
    searchIsError,
    searchData?.timed_out,
    searchPage,
    searchPagingKey,
    queryClient,
    municipalityId,
    exactSearchId,
    searchNorm,
    searchMode,
  ]);

  const showIntelligentSearchIndicator =
    !exactSearch && librarySearch.trim().length > 0 && (intelligentSearchHoldActive || searchLoading);
  const showExactSearchIndicator =
    exactSearch &&
    submittedExactQuery.length > 0 &&
    !exactPollExhausted &&
    (searchLoading || exactSearchInFlight(exactStatus));
  const showSearchIndicator = showIntelligentSearchIndicator || showExactSearchIndicator;
  const searchResultsVisible = searchActive && !showSearchIndicator;

  const searchFolderHits: FileLibraryFolderHit[] = useMemo(
    () => (searchResultsVisible ? (searchData?.folders ?? []) : []),
    [searchData, searchResultsVisible],
  );

  // Group search matches by folder (number) / ungrouped (null), and compute which folders to show.
  const searchDocsByFolder = useMemo(() => {
    const map = new Map<number, FileLibraryDocument[]>();
    if (!searchResultsVisible || !searchData) return map;
    for (const doc of searchData.documents) {
      if (doc.group_id == null) continue;
      const list = map.get(doc.group_id) ?? [];
      list.push(doc);
      map.set(doc.group_id, list);
    }
    return map;
  }, [searchData, searchResultsVisible]);

  const searchUngrouped = useMemo(
    () =>
      searchResultsVisible ? (searchData?.documents ?? []).filter((d) => d.group_id == null) : [],
    [searchData, searchResultsVisible],
  );

  // Search visibility: current-page document folders plus ancestors. Intelligent search also
  // opens semantic folder hits that have no documents on this page. Exact search ignores those
  // leftover hits so empty sibling years stay hidden.
  const visibleFolderIds = useMemo(() => {
    if (showSearchIndicator) return new Set<number>();
    if (!searchResultsVisible) {
      return searchActive ? new Set<number>() : null;
    }
    const visible = new Set<number>();
    for (const folderId of searchDocsByFolder.keys()) {
      for (const ancestor of ancestorChain(folderId, byId)) visible.add(ancestor);
    }
    if (!exactSearch) {
      for (const hit of searchFolderHits) {
        for (const ancestor of ancestorChain(hit.id, byId)) visible.add(ancestor);
      }
    }
    return visible;
  }, [
    showSearchIndicator,
    searchResultsVisible,
    searchActive,
    searchDocsByFolder,
    searchFolderHits,
    byId,
    exactSearch,
  ]);

  // Auto-expand the matching folders while searching.
  useEffect(() => {
    if (!searchResultsVisible || !visibleFolderIds) return;
    const next: Record<number, boolean> = {};
    for (const id of visibleFolderIds) next[id] = true;
    setFolderOpen(next);
    setUngroupedOpen(searchUngrouped.length > 0);
  }, [searchResultsVisible, visibleFolderIds, searchUngrouped.length]);

  // Leaving search: collapse back to the browsed folder's open chain.
  useEffect(() => {
    if (searchActive) return;
    setFolderOpen(() => {
      if (browseFolderId == null) return {};
      const open: Record<number, boolean> = {};
      for (const id of ancestorChain(browseFolderId, byId)) open[id] = true;
      return open;
    });
  }, [searchActive, browseFolderId, byId]);

  const handleFolderToggle = useCallback(
    (folderId: number, open: boolean) => {
      setFolderOpen((prev) => {
        const next = { ...prev };
        if (open) next[folderId] = true;
        else delete next[folderId];
        return next;
      });
      if (open && !selectedDoc && !searchActive) {
        setBrowseFolderId(folderId);
        setGridPage(1);
      }
    },
    [selectedDoc, searchActive],
  );

  const handleViewFolder = useCallback((folderId: number) => {
    setSelectedDoc(null);
    setBrowseFolderId(folderId);
    setGridPage(1);
  }, []);

  // Picking a folder search hit clears the query and drops into browse mode on that folder, so the
  // transition back to normal browsing works exactly like clicking any other folder (ENG-1540).
  const handlePickFolderHit = useCallback((folderId: number) => {
    setSelectedDoc(null);
    setLibrarySearch("");
    setSearchMode("intelligent");
    setSubmittedExactQuery("");
    setBrowseFolderId(folderId);
    setGridPage(1);
  }, []);

  const handleBrowseFolder = useCallback(
    (folderId: number | null) => {
      setBrowseFolderId(folderId);
      setGridPage(1);
      setFolderOpen(() => {
        if (folderId == null) return {};
        const open: Record<number, boolean> = {};
        for (const id of ancestorChain(folderId, byId)) open[id] = true;
        return open;
      });
    },
    [byId],
  );

  const handleCenterBrowseFolder = useCallback(
    (folderId: number | null) => {
      setSelectedDoc(null);
      handleBrowseFolder(folderId);
    },
    [handleBrowseFolder],
  );

  const handlePickDocument = useCallback(
    (doc: FileLibraryDocument) => {
      setSelectedDoc(doc);
      const groupId = doc.group_id ?? null;
      setBrowseFolderId(groupId);
      setGridPage(1);
      if (groupId == null) {
        setUngroupedOpen(true);
        return;
      }
      setFolderOpen(() => {
        const open: Record<number, boolean> = {};
        for (const id of ancestorChain(groupId, byId)) open[id] = true;
        return open;
      });
    },
    [byId],
  );

  const handleDocumentPreviewSidePanelOpenChange = useCallback((open: boolean) => {
    setDocumentPreviewSidePanelOpen(open);
    setLibrarySidebarCollapsed(open);
  }, []);

  useEffect(() => {
    setDocumentPreviewSidePanelOpen(false);
  }, [selectedDoc?.id]);

  useEffect(() => {
    if (!selectedDoc) setLibrarySidebarCollapsed(false);
  }, [selectedDoc]);

  const is_platform_admin = currentUser?.platform_admin === true;

  // Center grid inputs. document_ids on the browsed folder reflect the lazily-loaded page.
  const gridDocuments = searchResultsVisible
    ? (searchData?.documents ?? [])
    : searchActive || showSearchIndicator
      ? []
      : (browseDocs?.documents ?? []);
  const documentIdsByFolder = useMemo(() => {
    const map = new Map<number, number[]>();
    if (!searchActive && !showSearchIndicator && browseFolderId != null && browseDocs) {
      map.set(
        browseFolderId,
        browseDocs.documents.map((d) => d.id),
      );
    }
    return map;
  }, [searchActive, showSearchIndicator, browseFolderId, browseDocs]);
  const gridGroups = useMemo(
    () => foldersToGroups(folders, documentIdsByFolder),
    [folders, documentIdsByFolder],
  );
  const gridRootDocuments =
    !searchActive && !showSearchIndicator && browseFolderId == null
      ? (browseDocs?.documents ?? [])
      : [];
  const gridTotalPages =
    !searchActive && !showSearchIndicator ? (browseDocs?.metadata.total_pages ?? 1) : 1;
  const exactSearchMetadata =
    exactSearch && searchResultsVisible
      ? (searchData?.metadata ?? lastGoodExactMetadataRef.current)
      : undefined;
  const exactSearchTotalPages = exactSearchMetadata?.total_pages ?? 1;
  const showExactSearchPager =
    !selectedDoc && exactSearch && searchResultsVisible && exactSearchTotalPages > 1;
  const showBrowsePager = !selectedDoc && !searchActive && gridTotalPages > 1;
  const pagerTotalPages = showExactSearchPager ? exactSearchTotalPages : gridTotalPages;
  const pagerPage = showExactSearchPager
    ? (exactSearchMetadata?.current_page ?? searchPage)
    : gridPage;

  const libraryLocationCount =
    tree ? folders.length + (tree.ungrouped_count > 0 ? 1 : 0) : 0;

  const fileCenterLead = tree ? (
    <p className="shrink-0 text-sm text-muted-foreground">
      Searching <FileCenterCount count={tree.total_count} />{" "}
      {fileCenterCountLabel(tree.total_count, "document", "documents")} across{" "}
      <FileCenterCount count={libraryLocationCount} />{" "}
      {fileCenterCountLabel(libraryLocationCount, "folder", "folders")}
    </p>
  ) : null;

  const fileCenterInsight = useMemo(() => {
    if (!tree) return null;
    if (tree.total_count === 0) {
      return is_platform_admin
        ? "No documents yet for this municipality. Uploaded PDFs will show up here once they're added."
        : "Your library is empty. Contact your team to upload documents for this municipality.";
    }
    if (tree.ungrouped_count > 0 && folders.length > 0) {
      return `${formatFileCenterCount(tree.ungrouped_count)} document${tree.ungrouped_count === 1 ? "" : "s"} are at the library root. Move them into folders when you are ready to organize the collection.`;
    }
    if (tree.chat_ready_count === 0) {
      return "No files are ready for chat yet—wait for page counts or open PDFs that are within the chat page limit.";
    }
    return null;
  }, [tree, folders.length, is_platform_admin]);

  const advancedFields = useMemo(
    () => fieldsFromSearchBar(librarySearch, exactSearch),
    [librarySearch, exactSearch],
  );

  const handleLibrarySearchChange = useCallback((value: string) => {
    setLibrarySearch(value);
    if (value.trim().length === 0) {
      setSearchMode("intelligent");
      setSubmittedExactQuery("");
    }
  }, []);

  const handleExitExactSearch = useCallback(() => {
    if (librarySearch.trim().length > 0) {
      setLibrarySearch(positiveQueryFromExactSearch(librarySearch));
    }
    setSearchMode("intelligent");
    setSubmittedExactQuery("");
  }, [librarySearch]);

  const handleAdvancedSearch = useCallback((composed: string, folderIds: number[], nextIncludeUngrouped: boolean) => {
    const next = normalizeQuery(composed);
    setSearchMode("exact");
    setLibrarySearch(composed);
    setSubmittedExactQuery(next);
    setExactFolderIds(folderIds);
    setIncludeUngrouped(nextIncludeUngrouped);
    setExactSearchNonce((current) => current + 1);
  }, []);

  const clearExactFolders = useCallback(() => {
    setExactFolderIds([]);
    setIncludeUngrouped(false);
  }, []);

  const handleExactSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== "Enter" || !exactSearch) return;
      event.preventDefault();
      const next = normalizeQuery(librarySearch);
      if (next.length === 0) return;
      setSubmittedExactQuery(next);
      setExactSearchNonce((current) => current + 1);
    },
    [exactSearch, librarySearch],
  );

  const searchControls = (
    <div className="flex min-w-0 w-full max-w-2xl flex-1 items-center gap-2">
      <Label htmlFor="file-viewer-search" className="sr-only">
        Search files
      </Label>
      <div className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-input bg-background px-3 shadow-sm transition-colors focus-within:ring-1 focus-within:ring-ring">
        {showSearchIndicator ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden />
        ) : (
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        )}
        <Input
          id="file-viewer-search"
          type="search"
          placeholder="Search files and folders..."
          value={librarySearch}
          onChange={(event) => handleLibrarySearchChange(event.target.value)}
          onKeyDown={handleExactSearchKeyDown}
          className="h-9 min-w-0 flex-1 border-0 bg-transparent px-0 tracking-normal shadow-none placeholder:tracking-normal focus-visible:ring-0"
          autoComplete="off"
        />
      </div>
      {exactSearch && librarySearch.trim().length > 0 ? (
        <button
          type="button"
          onClick={handleExitExactSearch}
          aria-label="Switch to intelligent search"
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-transparent bg-secondary px-2 py-1 text-xs font-semibold text-secondary-foreground hover:bg-secondary/80"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
          Exact
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
      <Button
        type="button"
        variant="outline"
        className="shrink-0 gap-2 font-bold"
        onClick={() => setAdvancedSearchOpen(true)}
      >
        <SlidersHorizontal className="h-4 w-4" aria-hidden />
        Advanced Search
      </Button>
    </div>
  );

  const exactScopeText = exactSearchScopeText(exactFolderIds, folders, includeUngrouped);
  const exactFolderScope =
    exactSearch && exactScopeText.length > 0 ? (
      <div className="flex min-w-0 w-full max-w-2xl flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span>Searching in {exactScopeText}</span>
        <button
          type="button"
          className="font-medium text-primary hover:underline"
          onClick={clearExactFolders}
        >
          Clear folders
        </button>
      </div>
    ) : null;

  if (!municipalitiesLoading && municipalities.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-muted-foreground">
        No municipalities are available for your account.
      </div>
    );
  }

  const showRootFolders = rootFolders.filter(
    (f) => visibleFolderIds == null || visibleFolderIds.has(f.id),
  );
  const ungroupedDocs = searchResultsVisible ? searchUngrouped : [];
  const hasContent = folders.length > 0 || (tree?.total_count ?? 0) > 0;

  const searchSettled =
    searchResultsVisible && searchSucceeded && searchData?.query === searchNorm;

  // Search-result states, kept explicit so the sidebar can branch on loading / error / empty
  // without recomputing the same conditions inline (ENG-1540).
  const searchHasResults =
    searchSettled &&
    ((searchData.folders?.length ?? 0) > 0 || (searchData.documents?.length ?? 0) > 0);
  const searchLimited = searchSettled && searchData?.limited === true;
  const searchTimedOut = searchSettled && searchData?.timed_out === true;
  const searchEmpty = searchSettled && !searchHasResults && !searchLimited && !searchTimedOut;
  const searchErrorMessage = exactPollExhausted
    ? "This search is taking too long. Try fewer words or a more specific phrase."
    : clerkFacingSearchError(exactStartQuery.error ?? exactResultQuery.error);
  const gridEmptyMessage = searchTimedOut ? (
    "Search timed out. Try fewer words or a more specific phrase."
  ) : searchLimited ? (
    "Too many matching documents. Narrow your search and try again."
  ) : searchEmpty ? (
    `No results for “${searchNorm}”.`
  ) : searchResultsVisible && searchIsError ? (
    searchErrorMessage
  ) : browseFetching ? (
    "Loading…"
  ) : browseFolderId == null ? (
    tree?.total_count === 0 ? "No files in this library yet." : "Select a folder or file."
  ) : undefined;

  return (
    <div className={cn(moduleWorkspaceShellClass, documentPreviewFocus ? "bg-background" : "bg-muted/30")}>
      <ModuleWorkspaceHeader
        title="File Center"
        density={documentPreviewFocus ? "compact" : "default"}
        subtitle="Browse PDFs uploaded for your municipality."
        actions={<MunicipalitySwitcher />}
        summary={
          municipalityId != null && !municipalitiesLoading
            ? {
              lead: (
                <div className="flex min-w-0 w-full flex-wrap items-center gap-x-4 gap-y-2">
                  {treeLoading ? <Skeleton className="h-4 w-56 shrink-0" /> : fileCenterLead}
                  <div className="flex min-w-0 flex-1 flex-col items-center gap-1">
                    {searchControls}
                    {exactFolderScope}
                  </div>
                </div>
              ),
              insight: fileCenterInsight,
              testId: "file-center-module-summary",
            }
            : undefined
        }
      />

      {municipalitiesLoading && (
        <div className="flex flex-1 items-center justify-center p-8 text-muted-foreground">
          <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
          <span className="sr-only">Loading municipalities</span>
        </div>
      )}

      {!municipalitiesLoading && !municipality && (
        <div className="flex flex-1 items-center justify-center p-8 text-muted-foreground">
          Choose a municipality to load files.
        </div>
      )}

      {!municipalitiesLoading && municipality && !fileViewerEnabled && (
        <div className="flex flex-1 items-center justify-center p-8 text-muted-foreground text-center text-sm max-w-md mx-auto">
          File Center is not enabled for your account. Contact your Customer Success Manager to unlock
          this feature, or switch to a municipality where it is already available.
        </div>
      )}

      {!municipalitiesLoading && municipalityId && (
        <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
          {librarySidebarCollapsed && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="absolute left-2 top-3 z-20 shadow-md"
              onClick={() => setLibrarySidebarCollapsed(false)}
            >
              <PanelLeftOpen className="h-4 w-4 mr-1.5 shrink-0" aria-hidden />
              Files
            </Button>
          )}
          <aside
            data-testid="file-center-library-sidebar"
            className={cn(
              "flex w-64 min-w-64 max-w-64 shrink-0 flex-col overflow-hidden border-r bg-background min-h-0",
              librarySidebarCollapsed && "hidden",
            )}
          >
            <div className="relative min-h-0 flex-1 overflow-hidden">
              <ScrollArea
                className="h-full min-h-0 w-full overflow-hidden"
                viewportClassName={FILE_CENTER_SIDEBAR_SCROLL_VIEWPORT_CLASS}
                scrollbarSize="lg"
              >
                <FileCenterTooltipProvider>
                  <div className="box-border w-full min-w-0 max-w-full space-y-0.5 overflow-hidden pt-1 pb-2 pl-2 pr-3">
                    {treeLoading && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground p-3">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading…
                      </div>
                    )}
                    {isError && (
                      <p className="text-sm text-destructive p-3">
                        {error instanceof Error ? error.message : "Could not load files."}
                      </p>
                    )}
                    {!treeLoading && !isError && !hasContent && (
                      <p className="text-sm text-muted-foreground p-3">No files in this library yet.</p>
                    )}
                    {!treeLoading && !isError && showIntelligentSearchIndicator && (
                      <IntelligentSearchIndicator className="p-3 text-sm -translate-y-1.5" />
                    )}
                    {!treeLoading && !isError && showExactSearchIndicator && (
                      <ExactSearchIndicator
                        key={`${submittedExactQuery}:${exactSearchNonce}`}
                        className="p-3 text-sm -translate-y-1.5"
                        label="Searching exact matches..."
                      />
                    )}
                    {!treeLoading && !isError && searchResultsVisible && searchIsError && (
                      <p className="text-sm text-destructive p-3 -translate-y-1.5" role="status" aria-live="polite">
                        {searchErrorMessage}
                      </p>
                    )}
                    {searchResultsVisible &&
                      exactSearch &&
                      failedExactPageNote != null &&
                      searchPage === failedExactPageNote.restoredPage && (
                      <p
                        className="text-sm text-muted-foreground p-3 -translate-y-1.5"
                        role="status"
                        aria-live="polite"
                      >
                        Page {failedExactPageNote.failedPage} couldn't load. Try again.
                      </p>
                    )}
                    {searchTimedOut && (
                      <p className="text-sm text-muted-foreground p-3 -translate-y-1.5" role="status" aria-live="polite">
                        Search timed out. Try fewer words or a more specific phrase.
                      </p>
                    )}
                    {searchLimited && (
                      <p className="text-sm text-muted-foreground p-3 -translate-y-1.5" role="status" aria-live="polite">
                        Too many matching documents. Narrow your search and try again.
                      </p>
                    )}
                    {searchEmpty && (
                      <p className="text-sm text-muted-foreground p-3 -translate-y-1.5" role="status" aria-live="polite">
                        No results for “{searchNorm}”.
                      </p>
                    )}
                    {searchResultsVisible &&
                      exactSearch &&
                      (searchData?.total_match_count ?? 0) > 0 &&
                      searchData?.documents &&
                      searchData.documents.length > 0 && (
                      <p className="px-3 pb-2 text-xs text-muted-foreground">
                        {exactMatchRangeCopy(
                          searchData.total_match_count ?? 0,
                          exactSearchMetadata?.current_page ?? searchPage,
                          exactSearchMetadata?.per_page ?? GRID_PAGE_SIZE,
                          searchData.documents.length,
                        )}
                      </p>
                    )}
                    {!treeLoading &&
                      !isError &&
                      searchResultsVisible &&
                      !exactSearch &&
                      searchFolderHits.length > 0 && (
                      <div className="mb-1">
                        <p className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Matched folders
                        </p>
                        <ul className="min-w-0 max-w-full space-y-0.5 overflow-hidden">
                          {searchFolderHits.map((hit) => (
                            <li key={hit.id} className="min-w-0 max-w-full overflow-hidden">
                              <FolderHitRow hit={hit} onPick={() => handlePickFolderHit(hit.id)} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {!treeLoading &&
                      !isError &&
                      !showSearchIndicator &&
                      showRootFolders.map((folder) => (
                        <FolderBranch
                          key={folder.id}
                          folder={folder}
                          childrenByParent={childrenByParent}
                          municipalityId={municipalityId!}
                          open={folderOpen[folder.id] ?? false}
                          folderOpen={folderOpen}
                          onFolderToggle={handleFolderToggle}
                          selectedDoc={selectedDoc}
                          onPickDocument={handlePickDocument}
                          onViewFolder={handleViewFolder}
                          searchActive={searchResultsVisible}
                          searchDocsByFolder={searchDocsByFolder}
                          visibleFolderIds={visibleFolderIds}
                        />
                      ))}
                    {!treeLoading && !isError && searchResultsVisible && ungroupedDocs.length > 0 && (
                      <Collapsible open={ungroupedOpen} onOpenChange={setUngroupedOpen} className="group w-full min-w-0">
                        <div className="relative w-full min-w-0">
                          <CollapsibleTrigger type="button" className={FILE_CENTER_FOLDER_ROW_CLASS}>
                            <ChevronRight
                              className="h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-90"
                              aria-hidden
                            />
                            <Folder className="h-4 w-4 shrink-0" aria-hidden />
                            <span className="min-w-0 flex-1 truncate">Ungrouped</span>
                          </CollapsibleTrigger>
                          <span className={FILE_CENTER_FOLDER_COUNT_CLASS}>{ungroupedDocs.length}</span>
                        </div>
                        <CollapsibleContent className="min-w-0 overflow-hidden">
                          <ul className="mt-0.5 w-full min-w-0 max-w-full overflow-hidden space-y-0.5 border-l border-border/70 pl-3">
                            {ungroupedDocs.map((doc) => (
                              <li key={doc.id} className="min-w-0">
                                <SidebarFileButton
                                  doc={doc}
                                  selected={selectedDoc?.id === doc.id}
                                  onPick={() => handlePickDocument(doc)}
                                />
                              </li>
                            ))}
                          </ul>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                </FileCenterTooltipProvider>
              </ScrollArea>
            </div>
          </aside>
          {!treeLoading && !isError && tree && (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
              <FileCenterFolderGrid
                groups={searchResultsVisible ? [] : gridGroups}
                documents={gridDocuments}
                rootDocuments={searchResultsVisible ? gridDocuments : gridRootDocuments}
                rootLabel="Library"
                browseFolderId={searchResultsVisible ? null : browseFolderId}
                onBrowseFolder={handleCenterBrowseFolder}
                onPickDocument={handlePickDocument}
                selectedDocId={selectedDoc?.id ?? null}
                previewDocument={selectedDoc}
                onClosePreview={() => setSelectedDoc(null)}
                emptyMessage={gridEmptyMessage}
                municipalityId={municipalityId}
                onSidePanelOpenChange={handleDocumentPreviewSidePanelOpenChange}
                className={librarySidebarCollapsed ? "border-r-0" : undefined}
              />
              {(showBrowsePager || showExactSearchPager) && (
                <div className="flex items-center justify-center gap-3 border-t px-4 py-2 text-sm">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={pagerPage <= 1 || (showExactSearchPager && searchIsPlaceholderData)}
                    onClick={() =>
                      showExactSearchPager
                        ? setSearchPaging({
                            key: searchPagingKey,
                            page: Math.max(1, pagerPage - 1),
                          })
                        : setGridPage((p) => Math.max(1, p - 1))
                    }
                  >
                    Previous
                  </Button>
                  <span
                    role="status"
                    aria-live="polite"
                    className="tabular-nums text-muted-foreground"
                  >
                    Page {pagerPage} of {pagerTotalPages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={
                      pagerPage >= pagerTotalPages ||
                      (showExactSearchPager && searchIsPlaceholderData)
                    }
                    onClick={() =>
                      showExactSearchPager
                        ? setSearchPaging({
                            key: searchPagingKey,
                            page: Math.min(pagerTotalPages, pagerPage + 1),
                          })
                        : setGridPage((p) => Math.min(gridTotalPages, p + 1))
                    }
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <FileCenterAdvancedSearchModal
        open={advancedSearchOpen}
        municipalityId={municipalityId ?? undefined}
        initialFields={advancedFields}
        folders={folders}
        selectedFolderIds={exactFolderIds}
        ungroupedCount={tree?.ungrouped_count ?? 0}
        includeUngrouped={includeUngrouped}
        onOpenChange={setAdvancedSearchOpen}
        onSearch={handleAdvancedSearch}
      />
    </div>
  );
}
